import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface Option {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  options: Option[];
  value: string;
  placeholder?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  name?: string;
  label?: string;
  required?: boolean;
}

const DropdownContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  font-family: var(--font-family-main);
  font-weight: 400;
  font-size: 14px;
  color: var(--color-text-secondary);
  margin-bottom: 4px;
`;

const DropdownButton = styled.button<{ $isOpen: boolean; $hasValue: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${props => props.$isOpen ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.12)'};
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.04);
  color: ${props => props.$hasValue ? 'var(--color-text)' : 'rgba(255, 255, 255, 0.4)'};
  font-family: var(--font-family-main);
  font-size: 16px;
  line-height: 1.5;
  min-height: 48px;
  transition: all 0.2s ease;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.$isOpen ? 'var(--color-primary)' : 'rgba(255, 255, 255, 0.24)'};
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-primary);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DropdownArrow = styled.div<{ $isOpen: boolean }>`
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  
  svg {
    width: 100%;
    height: 100%;
    stroke: currentColor;
  }
`;

const DropdownList = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #0E1F1F;
  border: 1px solid var(--color-primary);
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-8px)'};
  transition: all 0.2s ease;
  
  /* Стилизация скроллбара */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 4px 0;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--color-primary);
    border-radius: 3px;
    border: 1px solid #0E1F1F;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #1e9e6b;
  }
  
  /* Для Firefox */
  scrollbar-width: thin;
  scrollbar-color: var(--color-primary) transparent;
`;

const DropdownOption = styled.div<{ $isSelected: boolean; $isHighlighted: boolean }>`
  padding: 12px 16px;
  color: var(--color-text);
  font-family: var(--font-family-main);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props =>
    props.$isSelected ? 'var(--color-primary)' :
    props.$isHighlighted ? '#172A2A' : 'transparent'};
  
  &:hover {
    background-color: ${props => props.$isSelected ? 'var(--color-primary)' : '#172A2A'};
  }
  
  &:first-child {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
  
  &:last-child {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
`;

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  options,
  value,
  placeholder = "Выберите опцию",
  disabled = false,
  onChange,
  name,
  label,
  required = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option?.value === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && selectedOption) {
      const selectedIndex = options.findIndex(option => option.value === value);
      setHighlightedIndex(selectedIndex);
    }
  }, [isOpen, options, value, selectedOption]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setHighlightedIndex(-1);
      }
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isOpen && highlightedIndex >= 0 && options[highlightedIndex]) {
          handleSelect(options[highlightedIndex].value);
        } else {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (options.length > 0) {
          setHighlightedIndex(prev => 
            prev < options.length - 1 ? prev + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else if (options.length > 0) {
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : options.length - 1
          );
        }
        break;
    }
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      {label && <Label>{label}{required && ' *'}</Label>}
      <DropdownButton
        type="button"
        $isOpen={isOpen}
        $hasValue={!!selectedOption}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      >
        <span>{selectedOption?.label || placeholder}</span>
        <DropdownArrow $isOpen={isOpen}>
          <svg fill="none" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
        </DropdownArrow>
      </DropdownButton>
      
      <DropdownList $isOpen={isOpen}>
        {options.map((option, index) => (
          <DropdownOption
            key={option?.value || index}
            $isSelected={option?.value === value}
            $isHighlighted={index === highlightedIndex}
            onClick={() => handleSelect(option?.value || '')}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            {option?.label || ''}
          </DropdownOption>
        ))}
      </DropdownList>
    </DropdownContainer>
  );
};

export default CustomDropdown; 